import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Download } from 'lucide-react';
import { formatPhoneWithCountryCode, formatPhoneForLink, generateVCard, downloadVCard } from '../../utils/signature/formatUtils';
import { splitAddressLines } from '../../utils/signature/addressUtils';
import { getSizeTokens } from '../../utils/signature/sizeUtils';

export const SignaturePreview = ({ signatureData, visibilitySettings, previewId = 'signature-preview', showVCard = true }) => {
  const [showVCardButton, setShowVCardButton] = useState(false);

  const brandColor = signatureData.brandColor || '#EB2F5B';
  const size = getSizeTokens(signatureData.signatureSize, {
    bannerSize: signatureData.bannerSize,
    bannerWidth: signatureData.bannerWidth,
  });
  const px = (value) => `${value}px`;
  const labelScope = signatureData.fieldLabelsScope || (signatureData.showFieldLabels === false ? 'none' : 'all');
  const showPhoneLabels = labelScope !== 'none';
  const showOtherLabels = labelScope === 'all';
  const displayPhone = (value) =>
    signatureData.showCountryCode === false ? value : formatPhoneWithCountryCode(value, signatureData.countryCode);
  const officeLabel = signatureData.officeLabel ?? 'Office';
  const mobileLabel = signatureData.mobileLabel ?? 'Mobile';
  const labelStyle = (isPhoneRow) => {
    const matched =
      signatureData.labelStyle === 'match' && (isPhoneRow || signatureData.labelStyleScope === 'all');
    return {
      color: matched ? '#4b5563' : brandColor,
      fontWeight: matched ? 'normal' : 'bold',
      fontSize: px(size.bodySize),
      lineHeight: '1.15',
      paddingRight: '5px',
    };
  };

  const getImagePath = (imageUrl) => {
    if (!imageUrl) return `${window.location.origin}/defaults/profile-placeholder.svg`;
    if (imageUrl.startsWith('http') || imageUrl.startsWith('data:')) return imageUrl;
    // Images from /uploads/ should use the current origin
    if (imageUrl.startsWith('/uploads/')) return `${window.location.origin}${imageUrl}`;
    if (imageUrl.startsWith('/api/')) return `${window.location.origin}${imageUrl}`;
    if (imageUrl.startsWith('/')) return `${window.location.origin}${imageUrl}`;
    return `${window.location.origin}/${imageUrl}`;
  };

  const addressLines = splitAddressLines(signatureData.address);

  const hasSocialMedia =
    ((visibilitySettings.linkedin !== false) && signatureData.linkedin) ||
    ((visibilitySettings.xUsername !== false) && signatureData.xUsername) ||
    ((visibilitySettings.facebook !== false) && signatureData.facebook) ||
    ((visibilitySettings.instagram !== false) && signatureData.instagram) ||
    ((visibilitySettings.youtube !== false) && signatureData.youtube);

  return (
    <div
      id={previewId}
      className="font-sans text-sm relative"
      onMouseEnter={() => showVCard && setShowVCardButton(true)}
      onMouseLeave={() => setShowVCardButton(false)}
    >
      <table cellPadding="0" cellSpacing="0" border="0" style={{ fontFamily: 'Arial, sans-serif', maxWidth: px(size.maxWidth) }}>
        <tbody>
          <tr>
            {/* Profile image and logo column */}
            {(((visibilitySettings.profileImage !== false) && signatureData.profileImage) ||
              ((visibilitySettings.companyLogo !== false) && signatureData.companyLogo)) && (
              <td align="center" valign="top" style={{ paddingRight: px(size.columnGap), width: px(size.mediaColumn) }} width={size.mediaColumn}>
                {/* Profile Image */}
                {(visibilitySettings.profileImage !== false) && signatureData.profileImage && (
                  <table cellPadding="0" cellSpacing="0" border="0" width={size.mediaColumn} style={{ width: px(size.mediaColumn) }}>
                    <tbody>
                      <tr>
                        <td align="center" style={{ paddingBottom: '10px' }}>
                          <img
                            src={getImagePath(signatureData.profileImage)}
                            alt=""
                            width={size.photoSize}
                            height={size.photoSize}
                            style={{ 
                              width: px(size.photoSize), 
                              height: px(size.photoSize), 
                              borderRadius: '50%',
                              display: 'block',
                              border: '0'
                            }}
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                )}

                {/* Company Logo */}
                {(visibilitySettings.companyLogo !== false) && signatureData.companyLogo && (
                  <table cellPadding="0" cellSpacing="0" border="0" width={size.mediaColumn} style={{ width: px(size.mediaColumn) }}>
                    <tbody>
                      <tr>
                        <td
                          align="center"
                          valign="top"
                          style={{
                            paddingTop: (visibilitySettings.profileImage !== false) && signatureData.profileImage ? '10px' : '0',
                          }}
                        >
                          {signatureData.companyLogoLink ? (
                            <a href={signatureData.companyLogoLink} target="_blank" rel="noreferrer" style={{ textDecoration: 'none', border: '0' }}>
                              <img
                                src={getImagePath(signatureData.companyLogo)}
                                alt=""
                                width={size.logoMax}
                                style={{
                                  width: px(size.logoMax),
                                  maxWidth: '100%',
                                  maxHeight: px(size.logoMax),
                                  height: 'auto',
                                  display: 'block',
                                  border: '0'
                                }}
                              />
                            </a>
                          ) : (
                            <img
                              src={getImagePath(signatureData.companyLogo)}
                              alt=""
                              width={size.logoMax}
                              style={{
                                width: px(size.logoMax),
                                maxWidth: '100%',
                                maxHeight: px(size.logoMax),
                                height: 'auto',
                                display: 'block',
                                border: '0'
                              }}
                            />
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </td>
            )}

            {/* Vertical divider */}
            {(((visibilitySettings.profileImage !== false) && signatureData.profileImage) ||
              ((visibilitySettings.companyLogo !== false) && signatureData.companyLogo)) && (
              <td style={{ width: '1px', backgroundColor: '#e5e7eb', padding: '0', fontSize: '1px', lineHeight: '1px' }} width="1">&nbsp;</td>
            )}

            {/* Main content column */}
            <td
              valign="top"
              style={{
                paddingLeft:
                  ((visibilitySettings.profileImage !== false) && signatureData.profileImage) ||
                  ((visibilitySettings.companyLogo !== false) && signatureData.companyLogo)
                    ? px(size.columnGap)
                    : '0',
              }}
            >
              {/* Name and title */}
              {(visibilitySettings.fullName !== false) && signatureData.fullName && (
                <table cellPadding="0" cellSpacing="0" border="0" style={{ marginBottom: '2px' }}>
                  <tbody>
                    <tr>
                      <td style={{ fontSize: px(size.nameSize), fontWeight: 'bold', color: '#111827', lineHeight: '1.2' }}>
                        {signatureData.fullName}
                        {(visibilitySettings.credentials !== false) && signatureData.credentials && (
                          <span style={{ fontWeight: 'normal', color: '#4b5563' }}>, {signatureData.credentials}</span>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              )}

              {(visibilitySettings.jobTitle !== false) && signatureData.jobTitle && (
                <table cellPadding="0" cellSpacing="0" border="0" style={{ marginBottom: px(size.rowGap + 3) }}>
                  <tbody>
                    <tr>
                      <td style={{ fontSize: px(size.titleSize), color: brandColor, fontWeight: '600', lineHeight: '1.2' }}>
                        {signatureData.jobTitle}
                      </td>
                    </tr>
                  </tbody>
                </table>
              )}

              {/* Contact information */}
              <table cellPadding="0" cellSpacing="0" border="0" style={{ marginBottom: px(size.rowGap + 3), lineHeight: '1.15' }}>
                <tbody>
                  {(visibilitySettings.phoneNumber !== false) && signatureData.phoneNumber && (
                    <tr>
                      <td valign="top" style={{ paddingBottom: px(size.rowGap), minWidth: '100px', lineHeight: '1.15' }}>
                        {showPhoneLabels && officeLabel && (
                          <span style={labelStyle(true)}>
                            {officeLabel}:
                          </span>
                        )}
                        <a
                          href={`tel:${formatPhoneForLink(signatureData.phoneNumber)}`}
                          style={{ color: '#4b5563', textDecoration: 'none', fontSize: px(size.bodySize), lineHeight: '1.15', whiteSpace: 'nowrap' }}
                        >
                          {displayPhone(signatureData.phoneNumber)}
                        </a>
                      </td>
                    </tr>
                  )}

                  {(visibilitySettings.mobileNumber !== false) && signatureData.mobileNumber && (
                    <tr>
                      <td valign="top" style={{ paddingBottom: px(size.rowGap), minWidth: '100px', lineHeight: '1.15' }}>
                        {showPhoneLabels && mobileLabel && (
                          <span style={labelStyle(true)}>
                            {mobileLabel}:
                          </span>
                        )}
                        <a
                          href={`tel:${formatPhoneForLink(signatureData.mobileNumber)}`}
                          style={{ color: '#4b5563', textDecoration: 'none', fontSize: px(size.bodySize), lineHeight: '1.15', whiteSpace: 'nowrap' }}
                        >
                          {displayPhone(signatureData.mobileNumber)}
                        </a>
                      </td>
                    </tr>
                  )}

                  {(visibilitySettings.faxNumber !== false) && signatureData.faxNumber && (
                    <tr>
                      <td valign="top" style={{ paddingBottom: px(size.rowGap), lineHeight: '1.15' }}>
                        {showPhoneLabels && (
                          <span style={labelStyle(true)}>
                            Fax:
                          </span>
                        )}
                        <span style={{ color: '#4b5563', fontSize: px(size.bodySize), lineHeight: '1.15' }}>
                          {displayPhone(signatureData.faxNumber)}
                        </span>
                      </td>
                    </tr>
                  )}

                  {(visibilitySettings.email !== false) && signatureData.email && (
                    <tr>
                      <td valign="top" style={{ paddingBottom: px(size.rowGap), minWidth: '100px', lineHeight: '1.15' }}>
                        {showOtherLabels && (
                          <span style={labelStyle(false)}>
                            Email:
                          </span>
                        )}
                        <a
                          href={`mailto:${signatureData.email}`}
                          style={{ color: '#4b5563', textDecoration: 'none', fontSize: px(size.bodySize), lineHeight: '1.15', whiteSpace: 'nowrap' }}
                        >
                          {signatureData.email}
                        </a>
                      </td>
                    </tr>
                  )}

                  {(visibilitySettings.website !== false) && signatureData.website && (
                    <tr>
                      <td valign="top" style={{ paddingBottom: px(size.rowGap), minWidth: '100px', lineHeight: '1.15' }}>
                        {showOtherLabels && (
                          <span style={labelStyle(false)}>
                            Web:
                          </span>
                        )}
                        <a
                          href={`https://${signatureData.website.replace(/^https?:\/\//, '').replace(/^www\./, '')}`}
                          target="_blank"
                          style={{ color: '#4b5563', textDecoration: 'none', fontSize: px(size.bodySize), lineHeight: '1.15', whiteSpace: 'nowrap' }}
                          rel="noreferrer"
                        >
                          {signatureData.website.replace(/^https?:\/\//, '').replace(/^www\./, '')}
                        </a>
                      </td>
                    </tr>
                  )}

                  {(visibilitySettings.address !== false) && signatureData.address && (
                    <tr>
                      <td colSpan={2} valign="top" style={{ paddingBottom: px(size.rowGap), lineHeight: '1.15' }}>
                        <table cellPadding="0" cellSpacing="0" border="0" style={{ borderCollapse: 'collapse' }}>
                          <tbody>
                            <tr>
                              {showOtherLabels && (
                                <td valign="top" style={{ paddingRight: '5px', whiteSpace: 'nowrap' }}>
                                  <span style={{ ...labelStyle(false), paddingRight: 0 }}>
                                    Address:
                                  </span>
                                </td>
                              )}
                              <td valign="top">
                                {signatureData.addressLink ? (
                                  <a
                                    href={signatureData.addressLink}
                                    target="_blank"
                                    style={{ color: '#4b5563', textDecoration: 'none', fontSize: px(size.bodySize), lineHeight: '1.15' }}
                                    rel="noreferrer"
                                  >
                                    {addressLines[0]}
                                    {addressLines[1] && (
                                      <>
                                        <br />
                                        {addressLines[1]}
                                      </>
                                    )}
                                  </a>
                                ) : (
                                  <span style={{ color: '#4b5563', fontSize: px(size.bodySize), lineHeight: '1.15' }}>
                                    {addressLines[0]}
                                    {addressLines[1] && (
                                      <>
                                        <br />
                                        {addressLines[1]}
                                      </>
                                    )}
                                  </span>
                                )}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}

                  {(visibilitySettings.bookMeeting !== false) && signatureData.bookMeeting && (
                    <tr>
                      <td colSpan={2} valign="top" style={{ paddingBottom: px(size.rowGap), lineHeight: '1.15' }}>
                        {showOtherLabels && (
                          <span style={labelStyle(false)}>
                            Calendar:
                          </span>
                        )}
                        <a
                          href={signatureData.bookMeeting}
                          target="_blank"
                          style={{ color: '#4b5563', textDecoration: 'none', fontSize: px(size.bodySize), lineHeight: '1.15' }}
                          rel="noreferrer"
                        >
                          {signatureData.bookMeetingText || 'Book a Meeting'}
                        </a>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Social media icons */}
              {hasSocialMedia && (
                <table cellPadding="0" cellSpacing="0" border="0" style={{ paddingTop: px(size.rowGap), paddingBottom: px(size.rowGap) }}>
                  <tbody>
                    <tr>
                      <td>
                        <table cellPadding="0" cellSpacing="0" border="0">
                          <tbody>
                            <tr>
                        {(visibilitySettings.linkedin !== false) && signatureData.linkedin && (
                          <td style={{ paddingRight: '10px' }}>
                            <a
                              href={`https://linkedin.com/${signatureData.linkedin}`}
                              target="_blank"
                              rel="noreferrer"
                              style={{
                                display: 'inline-block',
                                width: px(size.socialIcon),
                                height: px(size.socialIcon),
                                backgroundColor: signatureData.useBrandColorForSocial ? brandColor : '#0A66C2',
                                borderRadius: '3px',
                                textAlign: 'center',
                                lineHeight: px(size.socialIcon),
                                color: 'white',
                                fontWeight: 'bold',
                                textDecoration: 'none',
                                fontSize: px(size.socialGlyph),
                              }}
                            >
                              in
                            </a>
                          </td>
                        )}

                        {(visibilitySettings.xUsername !== false) && signatureData.xUsername && (
                          <td style={{ paddingRight: '10px' }}>
                            <a
                              href={`https://x.com/${signatureData.xUsername}`}
                              target="_blank"
                              rel="noreferrer"
                              style={{
                                display: 'inline-block',
                                width: px(size.socialIcon),
                                height: px(size.socialIcon),
                                backgroundColor: signatureData.useBrandColorForSocial ? brandColor : '#000000',
                                borderRadius: '3px',
                                textAlign: 'center',
                                lineHeight: px(size.socialIcon),
                                color: 'white',
                                fontWeight: 'bold',
                                textDecoration: 'none',
                                fontSize: px(size.socialGlyph),
                              }}
                            >
                              X
                            </a>
                          </td>
                        )}

                        {(visibilitySettings.facebook !== false) && signatureData.facebook && (
                          <td style={{ paddingRight: '10px' }}>
                            <a
                              href={`https://facebook.com/${signatureData.facebook}`}
                              target="_blank"
                              rel="noreferrer"
                              style={{
                                display: 'inline-block',
                                width: px(size.socialIcon),
                                height: px(size.socialIcon),
                                backgroundColor: signatureData.useBrandColorForSocial ? brandColor : '#1877F2',
                                borderRadius: '3px',
                                textAlign: 'center',
                                lineHeight: px(size.socialIcon),
                                color: 'white',
                                fontWeight: 'bold',
                                textDecoration: 'none',
                                fontSize: px(size.socialGlyph),
                              }}
                            >
                              f
                            </a>
                          </td>
                        )}

                        {(visibilitySettings.instagram !== false) && signatureData.instagram && (
                          <td style={{ paddingRight: '10px' }}>
                            <a
                              href={`https://instagram.com/${signatureData.instagram}`}
                              target="_blank"
                              rel="noreferrer"
                              style={{
                                display: 'inline-block',
                                width: px(size.socialIcon),
                                height: px(size.socialIcon),
                                backgroundColor: signatureData.useBrandColorForSocial ? brandColor : '#E4405F',
                                borderRadius: '3px',
                                textAlign: 'center',
                                lineHeight: px(size.socialIcon),
                                color: 'white',
                                fontWeight: 'bold',
                                textDecoration: 'none',
                                fontSize: px(size.socialGlyph - 3),
                                fontFamily: 'Arial, sans-serif',
                              }}
                            >
                              IG
                            </a>
                          </td>
                        )}

                        {(visibilitySettings.youtube !== false) && signatureData.youtube && (
                          <td>
                            <a
                              href={`https://youtube.com/${signatureData.youtube}`}
                              target="_blank"
                              rel="noreferrer"
                              style={{
                                display: 'inline-block',
                                width: px(size.socialIcon),
                                height: px(size.socialIcon),
                                backgroundColor: signatureData.useBrandColorForSocial ? brandColor : '#FF0000',
                                borderRadius: '3px',
                                textAlign: 'center',
                                lineHeight: px(size.socialIcon),
                                color: 'white',
                                fontWeight: 'bold',
                                textDecoration: 'none',
                                fontSize: px(size.socialGlyph),
                                fontFamily: 'Arial, sans-serif',
                              }}
                            >
                              &#9658;
                            </a>
                          </td>
                        )}
                      </tr>
                    </tbody>
                  </table>
                </td>
              </tr>
            </tbody>
          </table>
              )}
            </td>
          </tr>

          {/* Banner row */}
          {(visibilitySettings.bannerImage !== false) && signatureData.bannerImage && (
            <tr>
              <td
                colSpan={
                  ((visibilitySettings.profileImage !== false) && signatureData.profileImage) ||
                  ((visibilitySettings.companyLogo !== false) && signatureData.companyLogo)
                    ? 3
                    : 1
                }
                style={{ paddingTop: px(size.blockGap), paddingBottom: '0' }}
              >
                <table cellPadding="0" cellSpacing="0" border="0" width="100%" style={{ maxWidth: px(size.maxWidth) }}>
                  <tbody>
                    <tr>
                      <td>
                        {signatureData.bannerLink ? (
                          <a href={signatureData.bannerLink} target="_blank" rel="noreferrer" style={{ textDecoration: 'none', border: '0' }}>
                            <img
                              src={getImagePath(signatureData.bannerImage)}
                              alt=""
                              width={size.bannerWidth}
                              style={{
                                maxWidth: '100%',
                                width: px(size.bannerWidth),
                                height: 'auto',
                                display: 'block',
                                border: '0'
                              }}
                            />
                          </a>
                        ) : (
                          <img
                            src={getImagePath(signatureData.bannerImage)}
                            alt=""
                            width={size.bannerWidth}
                            style={{
                              maxWidth: '100%',
                              width: px(size.bannerWidth),
                              height: 'auto',
                              display: 'block',
                              border: '0'
                            }}
                          />
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>
          )}

          {/* Disclaimer */}
          {(visibilitySettings.disclaimer !== false) && signatureData.disclaimer && (
            <tr>
              <td
                colSpan={
                  ((visibilitySettings.profileImage !== false) && signatureData.profileImage) ||
                  ((visibilitySettings.companyLogo !== false) && signatureData.companyLogo)
                    ? 3
                    : 1
                }
                style={{ paddingTop: px(size.blockGap) }}
              >
                <table cellPadding="0" cellSpacing="0" border="0" width="100%" style={{ maxWidth: px(size.maxWidth - 50) }}>
                  <tbody>
                    <tr>
                      <td style={{ borderTop: '1px solid #e5e7eb', paddingTop: '10px' }}>
                        <p style={{ fontSize: px(size.disclaimerSize), color: '#6b7280', margin: '0', lineHeight: '1.3', fontFamily: 'Arial, sans-serif' }}>
                          {signatureData.disclaimer}
                        </p>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {showVCard && showVCardButton && (
        <div
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            zIndex: 10,
            background: 'rgba(255,255,255,0.9)',
            borderRadius: '4px',
            padding: '4px',
            boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
          }}
        >
          <Button
            variant="outline"
            size="sm"
            onClick={() => downloadVCard(generateVCard(signatureData))}
            className="flex items-center gap-1 text-xs"
            title="Download contact information as vCard"
          >
            <Download size={12} />
            <span>Save Contact</span>
          </Button>
        </div>
      )}
    </div>
  );
};
